import {customElement, property, query, state} from 'lit/decorators.js';
import {css, html, LitElement, PropertyValues} from 'lit';
import {translate, TranslateService} from '@stefanholzapfel/lit-translate';
import {unsafeCSSIcon} from '../../../styles/icons';

import type {ComposeOption, ECharts} from 'echarts/core';
import * as echarts from 'echarts/core';
import {LineChart, LineSeriesOption} from 'echarts/charts';
import {
    DatasetComponent,
    GridComponent,
    GridComponentOption,
    TooltipComponent,
    TooltipComponentOption,
    TransformComponent,
} from 'echarts/components';
import {LabelLayout, UniversalTransition} from 'echarts/features';
import {CanvasRenderer} from 'echarts/renderers';
import {SpStatisticField} from '../sp-statistic-control/sp-statistic-control';
import {repeat} from 'lit-html/directives/repeat.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {SpHierarchicalSelectOption} from '../../inputs/sp-hierarchical-select/sp-hierarchical-select';
import {translateUppercase} from '@stefanholzapfel/lit-translate/dist/translateUppercase.directive';
import {when} from 'lit/directives/when.js';

type EChartsOption = ComposeOption<
    | LineSeriesOption
    | GridComponentOption
    | TooltipComponentOption
>;

echarts.use([
    LineChart,
    DatasetComponent,
    GridComponent,
    TooltipComponent,
    TransformComponent,
    LabelLayout,
    UniversalTransition,
    CanvasRenderer
]);

@customElement('sp-statistic-slide-field-flow')
export class SpStatisticSlideFieldFlow extends LitElement {

    private _chartInstance: ECharts | undefined;
    private _chartElemObserver: ResizeObserver | undefined;

    @property({type: String})
    intro: String;
    @property({type: Array})
    data: SpStatisticFieldFlowEntry[] = [];
    @property({type: Array})
    field: SpStatisticField;
    @property({type: Array})
    fieldValuesSelected: SpStatisticFieldValue[];
    @property({type: Function})
    valueToLabel?: (value: any, meta?: 'metaLabel' | 'infoSource' | 'infoImage') => string;
    @property({type: Boolean})
    loading: Boolean;
    @property({type: Boolean})
    chartInitialized: Boolean = false;

    @state()
    private _hasData: Boolean = false;
    @state()
    private _valueSelectValue: string | number;
    @state()
    private _valueSelectOptions: SpHierarchicalSelectOption[] = [];
    @query("#field-flow-chart", true)
    private _chartElem: HTMLDivElement | undefined | null;
    @query("sp-hierarchical-select", true)
    private _fieldValueSelect;
    @query("sp-default-dialog", true)
    private _defaultDialog;

    colors: string[] = ['#7499a0', '#c16667', '#b6976a', '#a07482', '#929f7a', '#d5ce91', '#9494ba', '#89bf9f', '#cf8888', '#97c0cd'];

    static styles = [
        css`
            :host {
                padding-bottom: 10px;
            }

            .description {
                margin: 15px;
                line-height: 18px;
            }
            
            .field {
                margin: 15px 15px 0 15px;
                line-height: 18px;
                font-weight: bold;
            }

            #field-flow-chart {
                width: 100%;
                height: 250px;
            }

            .list {
                width: 100%;
                padding: 0 15px;
                margin: 0;
                font-size: 14px;
                line-height: 18px;
                box-sizing: border-box;
            }

            .list td.pos {
                width: 25px;
            }

            .list td.total {
                width: 50px;
                padding-left: 20px;
                background-image: url(${unsafeCSSIcon('spotteron', {size: 'x128', color: 'grey-dark'})});
                background-position: left center;
                background-repeat: no-repeat;
                background-size: 14px auto;
            }

            .list td.color {
                width: 20px;
                cursor: pointer;
            }

            .list td.color div {
                width: 12px;
                height: 12px;
            }

            .list td.name {
                padding-right: 10px;
                cursor: pointer;
            }

            .list td.eye {
                width: 20px;
                background-image: url(${unsafeCSSIcon('eye', {size: 'x128', color: 'grey-light'})});
                background-position: right center;
                background-repeat: no-repeat;
                background-size: 16px auto;
                cursor: pointer;
            }

            .list td.eye.hidden {
                background-image: url(${unsafeCSSIcon('eye-slash', {size: 'x128', color: 'grey-light'})});
            }

            .list td.remove {
                width: 20px;
                background-image: url(${unsafeCSSIcon('close', {size: 'x128', color: 'grey-dark'})});
                background-position: right center;
                background-repeat: no-repeat;
                background-size: 12px auto;
                cursor: pointer;
            }

            .list .hidden td.name {
                text-decoration: line-through;
            }

            .add-category {
                display: block;
                color: #000;
                font-size: 14px;
                line-height: 18px;
                margin: 10px 15px 40px 15px;
                padding-left: 22px;
                background-image: url(${unsafeCSSIcon('plus', {size: 'x128', color: 'grey-dark'})});
                background-position: 2px center;
                background-repeat: no-repeat;
                background-size: 14px auto;
                cursor: pointer;
            }

            .no-data {
                padding: 15px;
            }
        `
    ];

    private updateEcharts() {
        let xAxisData = [];
        let seriesMap = {};
        let hasData = false;
        if (this.data != undefined) {
            this.data.forEach((elem, index) => {
                if (xAxisData.indexOf(elem.datetime) == -1) {
                    xAxisData.push(elem.datetime);
                }
                if (!seriesMap.hasOwnProperty(elem.value)) {
                    seriesMap[elem.value] = {
                        name: elem.value,
                        type: 'line',
                        smooth: true,
                        data: [],
                        color: this.colors[index],
                        connectNulls: true
                    };
                }
                let value = 0;
                switch(this.field.statisticConfig.aggregationType) {
                    case 'sum':
                        value = elem.sum;
                        break;
                    case 'avg':
                        value = elem.avg;
                        break;
                    case 'cnt':
                    case 'cnt_val':
                        value = elem.count;
                        break;
                }
                seriesMap[elem.value].data.push({
                    value: value
                });
                if (value != null) hasData = true;
            });
        }
        this._hasData = hasData;
        let series = [];
        if (this.field.statisticConfig.aggregationType == 'cnt_val') {
            this.fieldValuesSelected.forEach((v) => {
                if (!v.hidden) {
                    series.push(seriesMap[v.value]);
                }
            });
        } else {
            series.push(seriesMap[0]);
        }
        let option: EChartsOption = {
            grid: {
                top: "30px",
                bottom: "30px",
                left: "15px",
                right: "15px",
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: xAxisData,
                boundaryGap: false,
                nameLocation: 'middle'
            },
            yAxis: {
                type: 'value'
            },
            series: series
        };

        this._chartInstance?.setOption(option, true);
        if (!this.chartInitialized) {
            this.chartInitialized = true;
        }
    }

    private onChartElemResize = () => {
        this._chartInstance.resize();
    }

    protected firstUpdated(changedProperties: PropertyValues<this>): void {
        if (this._chartElem !== null && this._chartElem !== undefined) {
            this._chartInstance = echarts.init(this._chartElem);
            this._chartElemObserver = new ResizeObserver(this.onChartElemResize);
            this._chartElemObserver.observe(this._chartElem);
        }
    }

    protected willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has('field')) {
            this._hasData = false;
        }
        if (changedProperties.has('fieldValuesSelected')) {
            let values = this.fieldValuesSelected.map(v => v.value);
            this._valueSelectOptions = this.field.options.filter(o => {
                return values.indexOf(o.id) == -1;
            });
        }
    }

    protected updated(changedProperties: PropertyValues<this>): void {
        if (changedProperties.has('fieldValuesSelected')) {
            this.dispatchFieldValuesUpdate();
        }
        if (changedProperties.has('data') && this.fieldValuesSelected) {
            this.updateEcharts();
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this._chartInstance?.dispose();
        this._chartElemObserver.disconnect();
    }

    render() {
        return html`
            <div class="description">${ this.intro }</div>
            <div class="field">${ translate(`CONFIG_FIELD_${this.field.id}_LABEL`) } - ${ translate(`STATISTICS_FIELD_FLOW_AGGREGATION_TYPE_${ this.field.statisticConfig.aggregationType }`.toUpperCase())}:</div>
            ${ when(!this.loading && !this._hasData, () => html`
                <div class="no-data">
                    ${ translate('STATISTICS_NO_DATA') }
                </div>
            `)}
            <div id="field-flow-chart" style=${!this.chartInitialized || !this._hasData ? 'visibility:hidden;' : ''}></div>
            <table class="list">
                ${repeat(this.fieldValuesSelected ? this.fieldValuesSelected : [], (value, index) => html`
                    <tr class="${value.hidden ? 'hidden' : ''}">
                        <td class="color" @click=${() => {
                            this.toggleLineChartCategory(value)
                        }}>
                            <div style="background-color: ${this.colors[index]};"></div>
                        </td>
                        <td class="name" @click=${() => {
                            this.toggleLineChartCategory(value)
                        }}>
                            ${unsafeHTML(TranslateService.translate(`CONFIG_FIELD_${this.field.id}_OPTION_${value.value}`.toUpperCase()) as string)}
                        </td>
                        <td class="eye ${value.hidden ? 'hidden' : ''}"
                            @click=${() => {
                                this.toggleLineChartCategory(value)
                            }}></td>
                        <td class="remove" @click=${() => {
                            this.removeLineChartCategory(value)
                        }}></td>
                    </tr>
                `)}
            </table>
            <sp-hierarchical-select
                    style="display: none"
                    id="field-select"
                    .dialogHeader=${translate(`CONFIG_FIELD_${this.field.id}_LABEL`)}
                    .options=${this._valueSelectOptions}
                    .value=${this._valueSelectValue}
                    .valueToLabel=${this.valueToLabel}
                    required=true
                    .dialogButtonLabels=${
                            {
                                ok: translateUppercase('MODAL_OK'),
                                cancel: translateUppercase('MODAL_CANCEL'),
                                back: translateUppercase('MODAL_BACK')
                            }
                    }
                    @change=${(e) => {
                        this.onSelectValue(e.detail.value)
                    }}>
            </sp-hierarchical-select>
            ${ when(this.field.statisticConfig.aggregationType == 'cnt_val', () => html`
            <a class="add-category" @click=${this.openValueSelect}>
                ${translate('STATISTICS_FIELD_FLOW_ADD_CATEGORY')}
            </a>
            <sp-default-dialog
                    .headerText=${translate('MESSAGE_WARNING')}
                    .template=${translate('STATISTICS_FIELD_FLOW_ADD_CATEGORY_TOO_MANY')}
                    .buttons=${[{
                        text: "OK", clickCallback: (c, d) => d.hide(),
                        textPosition: 'center'
                    }]}>
            </sp-default-dialog>
            `)}
        `;
        // cat.hidden
    }

    openValueSelect() {
        // console.log(this._fieldValueSelect);
        // console.log(this._fieldValueSelect.find('div'));

        if (this.fieldValuesSelected && this.fieldValuesSelected.length >= 5) {
            //'MESSAGE_WARNING', 'STATISTICS_SPOT_FLOW_ADD_CATEGORY_TOO_MANY'
            this._defaultDialog.show();
        } else {
            this._fieldValueSelect.openModal();
        }
    }

    onSelectValue(value: string | number): void {
        //this._valueSelectValue = '';
        this._fieldValueSelect.value = null;
        this.fieldValuesSelected = [...this.fieldValuesSelected, {
            value: value,
            hidden: false
        }];
    }

    toggleLineChartCategory(value: SpStatisticFieldValue): void {
        value.hidden = !value.hidden;
        this.updateEcharts();
        this.requestUpdate();
        this.dispatchFieldValuesUpdate();
    }

    removeLineChartCategory(value: SpStatisticFieldValue): void {
        this.fieldValuesSelected = this.fieldValuesSelected.filter((v) => {
            return v.value != value.value;
        });
        // this.data = this.data.filter((elem) => {
        //     return elem.value != value.value;
        // });
        // this.updateEcharts();
        // this.dispatchFieldValuesUpdate();
    }

    private dispatchFieldValuesUpdate() {
        this.dispatchEvent(new CustomEvent<SpStatisticFieldValuesUpdatedEventPayload>('statistic-field-values-updated', {
            bubbles: true,
            cancelable: true,
            composed: true,
            detail: {fieldValues: this.fieldValuesSelected}
        }));
    }
}

export interface SpStatisticFieldValuesUpdatedEventPayload {
    fieldValues: SpStatisticFieldValue[]
}

export interface SpStatisticFieldFlowEntry {
    datetime: string;
    value?: number;
    count?: number;
    avg?: number;
    sum?: number;
}

export interface SpStatisticFieldValue {
    value: number | string,
    hidden: boolean
}